import { useEffect, useRef, useState } from 'react';
import { FILTER_POPUP_MARKER_ATTR } from './constants';

/**
 * Caps a picker dropdown's height to the surrounding filter popup.
 *
 * The picker dropdowns (`AssigneePicker`, `TagPicker`) are portaled
 * via Headless UI's `anchor` prop, so they sit outside the popup in
 * the DOM. Without a cap, they can grow taller than the popup itself
 * — which looks broken. This hook walks up from a ref attached to the
 * trigger to the popup root (marked via `FILTER_POPUP_MARKER_ATTR`),
 * measures it, and re-measures on resize. Consumers feed the value
 * into `--anchor-max-height` on the floating panel.
 *
 * Returns `popupHeight = undefined` when no popup is around (e.g. the
 * picker used standalone outside the filter popover) — callers should
 * treat that as "no cap".
 */
export const useFilterPopupHeight = () => {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const [popupHeight, setPopupHeight] = useState<number | undefined>();

    useEffect(() => {
        const popup = wrapperRef.current?.closest(
            `[${FILTER_POPUP_MARKER_ATTR}]`,
        );
        if (!(popup instanceof HTMLElement)) return;
        const sync = () => setPopupHeight(popup.offsetHeight);
        sync();
        const ro = new ResizeObserver(sync);
        ro.observe(popup);
        return () => ro.disconnect();
    }, []);

    return { wrapperRef, popupHeight };
};
